import * as React from "react";
import { Typography, Button, TextField } from "@material-ui/core";
import styled, { css } from "styled-components";
import { useTheme } from "@material-ui/styles";
import { Formik, FormikProps } from "formik";
import * as yup from "yup";
import { Theme } from "../../../theme";
import { useEffect, useState } from "react";

const StyledSection = styled.section`
  ${({ theme }: { theme: Theme }) => {
    return css`
      width: 700px;
    
      .form{
          padding-left:100px;
          padding-right:100px;
          padding-bottom:125px;
          flex-basis: 700px;
          ${theme.breakpoints.down("sm")}{
              padding-left: ${theme.spacing(2)}px
              padding-right: ${theme.spacing(2)}px
          }
      }
      .title{
          margin-top: ${theme.spacing(3)}px
      }
      .textField{
          margin-top: ${theme.spacing(1.5)}px
      }
      .signInButton{
          margin: ${theme.spacing(2, 0)}
      }
    `;
  }};
`;

const validationSchema = yup.object({
  email: yup
    .string()
    .email("Enter a valid email")
    .required("Email is required"),
  password: yup
    .string()
    .min(1)
    .required("Enter your password"),
});

interface Values {
  email: string;
  password: string;
}

export interface SigninFormProps {
  sharedEmail: string;
  goToSignup: () => void;
  goToForgotPassword: () => void;
  handleSignin: (email: string, password: string) => void;
}

const SigninForm: React.FC<SigninFormProps> = props => {
  const theme: Theme = useTheme();

  //Get rid of any timer before unmount
  const [timer, setTimer] = useState<any>(undefined);
  useEffect(() => {
    return () => {
      console.log("signin unmounting...");
      if (timer) clearTimeout(timer);
    };
  }, []);

  return (
    <>
      <StyledSection theme={theme}>
        <div className="form">
          <Formik
            initialValues={{ email: props.sharedEmail, password: "" }}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting }) => {
              await props.handleSignin(values.email, values.password);
              const timeout = setTimeout(() => {
                setSubmitting(false); //This is being called after unmount
              }, 500);
              setTimer(timeout);
            }}
          >
            {({
              values,
              touched,
              errors,
              dirty,
              isSubmitting,
              handleChange,
              handleBlur,
              handleSubmit,
              handleReset,
              submitForm,
            }: FormikProps<Values>) => (
              <>
                <Typography className="title" variant="h2">
                  Sign in
                </Typography>
                <Typography color="textSecondary" gutterBottom>
                  Sign in with your account
                </Typography>

                <TextField
                  autoFocus
                  autoComplete="email"
                  className="textField"
                  error={!!(errors.email && touched.email)}
                  fullWidth
                  helperText={errors.email && touched.email && errors.email}
                  label="Email address"
                  name="email"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  type="text"
                  value={values.email}
                  variant="outlined"
                />
                <TextField
                  className="textField"
                  error={!!(errors.password && touched.password)}
                  fullWidth
                  helperText={
                    errors.password && touched.password && errors.password
                  }
                  label="Password"
                  name="password"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  type="password"
                  value={values.password}
                  variant="outlined"
                />
                <Button
                  className="signInButton"
                  type="submit"
                  disabled={
                    isSubmitting || !dirty || Object.keys(errors).length > 0
                  }
                  onClick={submitForm}
                  color="primary"
                  fullWidth
                  size="large"
                  variant="contained"
                >
                  Sign in now
                </Button>
                <Typography
                  color="textSecondary"
                  variant="body1"
                  onClick={props.goToSignup}
                  style={{ cursor: "pointer" }}
                >
                  Don't have an account?
                </Typography>
                <Typography
                  color="textSecondary"
                  variant="body1"
                  onClick={props.goToForgotPassword}
                  style={{ cursor: "pointer" }}
                >
                  Forgot password, Reset here
                </Typography>
              </>
            )}
          </Formik>
        </div>
      </StyledSection>
    </>
  );
};

export default SigninForm;
